import React, { useMemo, useState } from "react";
import MainLayout from "../components/layout/MainLayout";
import Link from "next/link";
import Image from "next/image";
import { ProductItem } from "../components/common/ProductItem.comp";
import { LoadingComp } from "../components/common/loading.comp";
import { useLoadMoreOnIntersect } from "../components/common/useLoadMoreOnIntersect.hook";
import { useGuru } from "../components/context/guruContext";
import { useIsLogin } from "../core/cookie.core";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "next/router";
import profileCore from "../core/profile.core";
import { Product } from "../types/product";
import productCore from "../core/product.core";
import toast from "react-hot-toast";
import { Dialog } from "../components/checkout/routeChangeDialog";
import { useTranslation } from "../core/translation.core";
import useProductsGiftCard from "../hooks/useProductsGiftCard.hook";
import useProductsPointRedeemable from "../hooks/useProductsPointRedeemable.hook";

// Main Guru Fun Page Component
export default function GuruFunPage() {
  return (
    <MainLayout title="優惠券 | Guru Fun" url="/guru-fun">
      <GuruFunContent />
    </MainLayout>
  );
}

// Guru Fun Content Component with demo data focus
function GuruFunContent() {
  const isLogin = useIsLogin();
  const { t, msg } = useTranslation();
  const { isReady, asPath } = useRouter();
  const { profileData } = useGuru();
  const [selectedProduct, setSelectedProduct] = useState<Product>();

  const {
    data: giftCardsTypesIDs,
    isError: typeError,
    isPending: typePending,
  } = useQuery({
    queryKey: ["giftCardsTypesIDs"],
    queryFn: () => profileCore.getGiftCardsTypeIDs("guru-default"),
    enabled: isReady && isLogin,
  });

  const { data: giftResponse, isPending, isError } = useProductsGiftCard();
  const {
    data: redeemableResponse,
    isPending: redeemablePending,
    isError: redeemableError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useProductsPointRedeemable();

  const loadMoreRef = useLoadMoreOnIntersect({
    enabled: isReady && !!redeemableResponse?.list?.length,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  const coupons = useMemo(() => {
    if (!giftResponse?.list || !giftCardsTypesIDs) return [];

    const matchedSet = new Set(giftCardsTypesIDs.map(String));
    return giftResponse.list.filter((product) => {
      const defId = product.giftCardDefId?.toString();
      return defId && !matchedSet.has(defId);
    });
  }, [giftResponse, giftCardsTypesIDs]);

  if (!isLogin) {
    return (
      <div className="max-w-5xl mx-auto py-6 px-4 text-center">
        <p className="text-4xl">{t(msg.gift.loginRequired)}</p>
        <Link
          href={{
            pathname: "/auth",
            query: { page: "login", redirectTo: asPath },
          }}
          className="inline-block mt-6 bg-guru-milk-chocolate text-white px-5 py-2 rounded hover:bg-[#1a3c5a] transition-colors"
        >
          {t(msg.login.login)}
        </Link>
      </div>
    );
  }

  if (typePending || isPending || redeemablePending) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-80 bg-gray-100 animate-pulse rounded-lg" />
        ))}
      </div>
    );
  }

  if (isError || typeError || redeemableError) {
    return <span className="text-red-500 text-center">{t(msg.general.errSubmit)}</span>;
  }

  const handleRedeem = (selectedProduct: Product) => {
    if (!selectedProduct) {
      toast.error("Invalid product or points");
      return;
    }

    productCore
      .buyGiftCoupon(selectedProduct.giftCardDefId!, selectedProduct.price!.toString())
      .then(() => {
        // refreshGuruFun();
        setSelectedProduct(undefined);
        toast.success(t(msg.gift.buyCouponsuccess));
      })
      .catch((error) => {
        if (error.message.includes("rewardTooLarge")) {
          toast.error(t(msg.gift.buyGiftCouponError));
          return;
        }
        toast.error("Failed to buy coupon. Please try again.");
      });
  };

  if (!profileData) return null;

  return (
    <>
      <GuruFunHeader guruFun={profileData.userRewardPoints} />

      <div className="bg-[#f9f7f4] py-4 sm:py-6 md:py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          {redeemableResponse?.list && redeemableResponse.list.length > 0 ? (
            <div id="point-redemption-section" className="mt-4">
              <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 mb-3 md:mb-4">{t(msg.gift.redeemableProducts)}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1 sm:gap-2">
                {redeemableResponse.list.map((product) => (
                  <div key={product.id}>
                    <ProductItem product={product} updateItem={() => {}} disabled={!isLogin} showAddToCartButton={false} />
                  </div>
                ))}
              </div>
              <div ref={loadMoreRef} className="h-4 w-full" aria-hidden />
              {isFetchingNextPage ? (
                <div className="flex flex-col items-center gap-2 py-6">
                  <LoadingComp sizeClassName="h-8 w-8" />
                  <span className="text-sm text-gray-500">{t(msg.common.loadingMore)}</span>
                </div>
              ) : hasNextPage ? (
                <div className="flex justify-center py-6">
                  <button
                    type="button"
                    onClick={() => void fetchNextPage()}
                    className="bg-[#0c2237] text-white px-5 py-2 rounded text-sm hover:bg-[#1a3c5a] transition-colors"
                  >
                    {t(msg.common.loadMore)}
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}

          {coupons.length > 0 ? (
            <>
              <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 mb-3 md:mb-4 mt-8">優惠券/最低價惠</h2>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1 sm:gap-2">
                {coupons.map((product) => (
                  <div key={product.id}>
                    <ProductItem
                      key={product.id}
                      product={product}
                      updateItem={() => {}}
                      onRedeem={() => setSelectedProduct(product)}
                      disabled={!isLogin}
                      isCoupon={true}
                    />
                  </div>
                ))}
              </div>

              <Dialog
                title={t(msg.gift.buyGiftCoupon)}
                confirmLabel="Confirm"
                cancelLabel="Cancel"
                onConfirm={() => handleRedeem(selectedProduct!)}
                onReject={() => setSelectedProduct(undefined)}
                isOpen={!!selectedProduct}
              >
                <div className="p-4">{`${t(msg.gift.buyCouponDesc)} ${selectedProduct?.price} Guru Fun Points`}</div>
              </Dialog>

              <HowToRedeem />
            </>
          ) : null}
        </div>
      </div>
    </>
  );
}

// GuruFunHeader Component - matching exactly with G on left of Guru Fun text per image
function GuruFunHeader({ guruFun }: { guruFun: number }) {
  return (
    <div className="w-full bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="mr-2 sm:mr-3">
              <div className="relative w-8 h-8 sm:w-10 sm:h-10">
                <div className="rounded-full overflow-hidden border border-orange-100">
                  <Image src="/images/logo-square.webp" alt="G" width={40} height={40} className="object-contain" />
                </div>
              </div>
            </div>
            <div className="flex flex-col">
              <h1 className="text-base sm:text-lg font-medium text-neutral-800">Guru Fun</h1>
              <p className="text-lg sm:text-xl font-bold text-neutral-800">{guruFun}</p>
            </div>
          </div>
          <button className="bg-[#0c2237] text-white px-3 sm:px-5 py-1.5 sm:py-2 rounded text-xs sm:text-sm whitespace-nowrap">
            如何領取？
          </button>
        </div>
      </div>
    </div>
  );
}

// How To Redeem Section
function HowToRedeem() {
  return (
    <div className="bg-white p-4 sm:p-5 md:p-6 mt-6 sm:mt-8 rounded-sm">
      <h3 className="text-base sm:text-lg font-medium mb-2 sm:mb-3 md:mb-4">如何領取？</h3>
      <p className="text-gray-600 mb-3 sm:mb-4 text-sm md:text-base">
        以Guru
        Fun優惠券兌換優惠券需獨立操作，先進行兌換，然後喺購物車使用，每張優惠券嘅使用條款請參閱優惠券內嘅商品描述，一經退換後恕不退換!
      </p>
      <Link href="/article/shopping-guide" className="text-[#d05a37] hover:text-[#b14a2e] text-sm md:text-base">
        了解更多會員優惠 →
      </Link>
    </div>
  );
}
